import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "./api";
import { PageHeader, Card, Field, Input, Textarea, SaveBar } from "./components/Field";
import { ImageUpload } from "./components/ImageUpload";
import { usePreview } from "./PreviewContext";

// Schema-driven editor: which fields render for each section.
const SCHEMAS = {
  hero: {
    overline: "Section",
    title: "Hero",
    description: "Top of the homepage — headline, lead, CTAs, and background image.",
    fields: [
      { key: "overline", label: "Overline (small red label)", type: "text" },
      { key: "headline_top", label: "Headline — line 1", type: "text" },
      { key: "headline_bottom", label: "Headline — line 2 (solid)", type: "text" },
      { key: "headline_outline", label: "Headline — line 2 (outline word)", type: "text" },
      { key: "lead_bold", label: "Lead — bold opening", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "primary_cta_label", label: "Primary CTA label", type: "text" },
      { key: "primary_cta_url", label: "Primary CTA URL", type: "text", hint: "External link, e.g. Square booking URL." },
      { key: "secondary_cta_label", label: "Secondary CTA label", type: "text" },
      { key: "secondary_cta_anchor", label: "Secondary CTA anchor", type: "text", hint: "Use #newsletter to scroll to email signup." },
      { key: "side_label", label: "Side decorative label", type: "text" },
      { key: "status_label", label: "Status badge label", type: "text" },
      { key: "status_text", label: "Status helper text", type: "textarea" },
      { key: "image_url", label: "Hero background image", type: "image" },
    ],
  },
  philosophy: {
    overline: "Section",
    title: "Philosophy",
    description: "Heading, description, image block, and the four bento cards.",
    fields: [
      { key: "overline", label: "Overline", type: "text" },
      { key: "heading_top", label: "Heading — line 1", type: "text" },
      { key: "heading_accent", label: "Heading — accent word", type: "text" },
      { key: "heading_bottom", label: "Heading — line 3", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "image_url", label: "Big image (top of bento)", type: "image" },
      { key: "image_caption_overline", label: "Image overline", type: "text" },
      { key: "image_caption", label: "Image caption", type: "text" },
      { key: "block_1_number", label: "Card 1 — number", type: "text" },
      { key: "block_1_title", label: "Card 1 — title", type: "text" },
      { key: "block_1_body", label: "Card 1 — body", type: "textarea" },
      { key: "block_2_number", label: "Card 2 — number", type: "text" },
      { key: "block_2_title", label: "Card 2 — title", type: "text" },
      { key: "block_2_body", label: "Card 2 — body", type: "textarea" },
      { key: "manifesto_title", label: "Manifesto card — title", type: "text" },
      { key: "manifesto_body", label: "Manifesto card — body", type: "textarea" },
    ],
  },
  coach: {
    overline: "Section",
    title: "Coach Mike",
    description: "Bio, photo, and the credentials list.",
    fields: [
      { key: "overline", label: "Overline", type: "text" },
      { key: "heading_top", label: "Heading — line 1", type: "text" },
      { key: "heading_bottom", label: "Heading — line 2", type: "text" },
      { key: "bio", label: "Bio paragraph", type: "textarea", rows: 6 },
      { key: "image_url", label: "Photo of Coach Mike", type: "image" },
      { key: "photo_overline", label: "Photo overline", type: "text" },
      { key: "name_first", label: "Name — first line", type: "text" },
      { key: "name_accent", label: "Name — accent line", type: "text" },
      { key: "name_last", label: "Name — last line", type: "text" },
      { key: "credentials_label", label: "Credentials label", type: "text" },
      { key: "credentials", label: "Credentials list", type: "string-list", hint: "One credential per row." },
    ],
  },
  book: {
    overline: "Section",
    title: "The Book",
    description: "“A Defenseman's Guide to Elite Play” promo block.",
    fields: [
      { key: "overline_label", label: "Overline label", type: "text" },
      { key: "title_top", label: "Title — line 1", type: "text" },
      { key: "title_middle", label: "Title — line 2", type: "text" },
      { key: "title_accent", label: "Title — accent", type: "text" },
      { key: "description_main", label: "Main description", type: "textarea", rows: 5 },
      { key: "description_secondary", label: "Secondary description", type: "textarea" },
      { key: "rating_text", label: "Rating row text", type: "text" },
      { key: "primary_cta_label", label: "Primary CTA label", type: "text" },
      { key: "primary_cta_url", label: "Primary CTA URL", type: "text" },
      { key: "secondary_cta_label", label: "Secondary CTA label", type: "text" },
      { key: "image_url", label: "Book cover image", type: "image" },
    ],
  },
  ticker: {
    overline: "Section",
    title: "Ticker Words",
    description: "The scrolling marquee. Each entry becomes a word in the ticker.",
    fields: [
      { key: "words", label: "Marquee words", type: "string-list" },
    ],
  },
  logo_strip: {
    overline: "Section",
    title: "Accreditation Strip",
    description: "The short bar under the hero with accreditation and category pills.",
    fields: [
      { key: "accreditation_label", label: "Accreditation overline", type: "text" },
      { key: "accreditation_text", label: "Accreditation line", type: "text" },
      { key: "pills", label: "Category pills", type: "string-list", hint: "One per row. Shown as separated tags." },
    ],
  },
  programs_header: {
    overline: "Section",
    title: "Programs Section Header",
    description: "The intro at the top of the Programs grid (heading + description).",
    fields: [
      { key: "overline", label: "Overline", type: "text" },
      { key: "heading_top", label: "Heading — line 1", type: "text" },
      { key: "heading_accent", label: "Heading — accent (line 2)", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  newsletter: {
    overline: "Section",
    title: "Newsletter",
    description: "Email signup section copy and success message.",
    fields: [
      { key: "overline", label: "Overline", type: "text" },
      { key: "heading_top", label: "Heading — line 1", type: "text" },
      { key: "heading_accent", label: "Heading — accent", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "consent_text", label: "Consent disclaimer", type: "textarea" },
      { key: "submit_label", label: "Submit button label", type: "text" },
      { key: "success_title", label: "Success title", type: "text" },
      { key: "success_body", label: "Success message", type: "textarea" },
    ],
  },
  footer: {
    overline: "Section",
    title: "Footer",
    description: "Footer tagline and copyright lines.",
    fields: [
      { key: "tagline", label: "Footer tagline", type: "textarea" },
      { key: "copyright_left", label: "Copyright — left", type: "text" },
      { key: "copyright_right", label: "Copyright — right", type: "text" },
    ],
  },
  stats: {
    overline: "Section",
    title: "Stats Bar",
    description: "Four metrics displayed at the bottom of the hero.",
    fields: [
      { key: "items", label: "Stats", type: "stats-list" },
    ],
  },
  // ---- Standalone Pages ----
  page_about: {
    overline: "Page",
    title: "About / What is BDH",
    description: "Long-form story page at /about — hero, intro story, lessons, military section, 'Built Different' pull-quote, BDH standard, closing CTA.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "hero_image_url", label: "Hero background image", type: "image" },
      { key: "intro_label", label: "Intro — overline", type: "text" },
      { key: "intro_heading", label: "Intro — heading", type: "text" },
      { key: "intro_body", label: "Intro — body (paragraphs 1 & 2)", type: "textarea", rows: 12 },
      { key: "intro_image_url", label: "Intro — image", type: "image" },
      { key: "lessons_label", label: "Lessons — overline", type: "text" },
      { key: "lessons_heading", label: "Lessons — heading", type: "text" },
      { key: "lessons", label: "Lessons — bullet list", type: "string-list" },
      { key: "military_label", label: "Military — overline", type: "text" },
      { key: "military_heading", label: "Military — heading", type: "text" },
      { key: "military_body", label: "Military — body", type: "textarea", rows: 10 },
      { key: "military_image_url", label: "Military — image", type: "image" },
      { key: "definition_label", label: "'Built Different' — overline", type: "text" },
      { key: "definition_heading", label: "'Built Different' — heading", type: "text" },
      { key: "definition_body", label: "'Built Different' — body", type: "textarea", rows: 10 },
      { key: "development_label", label: "BDH Standard — overline", type: "text" },
      { key: "development_heading", label: "BDH Standard — heading", type: "text" },
      { key: "development_body", label: "BDH Standard — body", type: "textarea", rows: 10 },
      { key: "closing_label", label: "Closing — overline", type: "text" },
      { key: "closing_heading", label: "Closing — heading", type: "text" },
      { key: "closing_body", label: "Closing — body", type: "textarea", rows: 8 },
      { key: "cta_label", label: "Footer CTA label", type: "text" },
      { key: "cta_url", label: "Footer CTA URL", type: "text", hint: "Defaults to Site Settings Square URL if blank." },
    ],
  },
  page_philosophy: {
    overline: "Page",
    title: "Philosophy Page",
    description: "Hero, intro, four pillars, and the closing quote on /philosophy.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "hero_image_url", label: "Hero background image", type: "image" },
      { key: "intro_body", label: "Intro paragraph", type: "textarea", rows: 6 },
      { key: "pillar_1_title", label: "Pillar 1 — title", type: "text" },
      { key: "pillar_1_body", label: "Pillar 1 — body", type: "textarea" },
      { key: "pillar_2_title", label: "Pillar 2 — title", type: "text" },
      { key: "pillar_2_body", label: "Pillar 2 — body", type: "textarea" },
      { key: "pillar_3_title", label: "Pillar 3 — title", type: "text" },
      { key: "pillar_3_body", label: "Pillar 3 — body", type: "textarea" },
      { key: "pillar_4_title", label: "Pillar 4 — title", type: "text" },
      { key: "pillar_4_body", label: "Pillar 4 — body", type: "textarea" },
      { key: "quote", label: "Closing quote", type: "text" },
      { key: "quote_attribution", label: "Quote attribution", type: "text" },
      { key: "cta_label", label: "Footer CTA label", type: "text" },
      { key: "cta_url", label: "Footer CTA URL", type: "text", hint: "Defaults to Site Settings Square URL if left blank." },
    ],
  },
  page_programs: {
    overline: "Page",
    title: "Programs Page",
    description: "Hero + intro for /programs. The grid below is driven by the Programs manager.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "hero_image_url", label: "Hero background image", type: "image" },
      { key: "intro_body", label: "Intro paragraph", type: "textarea", rows: 5 },
    ],
  },
  page_coach: {
    overline: "Page",
    title: "Coach Page",
    description: "Full Coach Mike bio page at /coach.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_image_url", label: "Coach photo / hero image", type: "image" },
      { key: "intro_body", label: "Bio paragraph", type: "textarea", rows: 6 },
      { key: "section_2_title", label: "Block 2 — title", type: "text" },
      { key: "section_2_body", label: "Block 2 — body", type: "textarea" },
      { key: "section_3_title", label: "Block 3 — title", type: "text" },
      { key: "section_3_body", label: "Block 3 — body", type: "textarea" },
      { key: "section_4_title", label: "Block 4 — title", type: "text" },
      { key: "section_4_body", label: "Block 4 — body", type: "textarea" },
      { key: "cta_label", label: "Footer CTA label", type: "text" },
      { key: "cta_url", label: "Footer CTA URL", type: "text" },
    ],
  },
  page_book: {
    overline: "Page",
    title: "Book Page",
    description: "Dedicated /book page for “A Defenseman's Guide to Elite Play”.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "intro_body", label: "Intro paragraph", type: "textarea", rows: 5 },
      { key: "image_url", label: "Book cover", type: "image" },
      { key: "what_youll_learn_label", label: "“What You'll Learn” overline", type: "text" },
      { key: "what_youll_learn", label: "What you'll learn — list", type: "string-list" },
      { key: "audience_label", label: "“Who It's For” overline", type: "text" },
      { key: "audience_body", label: "“Who It's For” body", type: "textarea" },
      { key: "primary_cta_label", label: "Primary CTA label", type: "text" },
      { key: "primary_cta_url", label: "Primary CTA URL (Amazon)", type: "text" },
      { key: "secondary_cta_label", label: "Secondary CTA label", type: "text" },
    ],
  },
  page_contact: {
    overline: "Page",
    title: "Contact Page",
    description: "Copy for /contact (form submits to existing newsletter endpoint).",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "email_label", label: "Email card — label", type: "text" },
      { key: "email_value", label: "Email card — value", type: "text" },
      { key: "response_time_label", label: "Response time — label", type: "text" },
      { key: "response_time_value", label: "Response time — value", type: "text" },
      { key: "booking_label", label: "Booking card — heading", type: "text" },
      { key: "booking_body", label: "Booking card — body", type: "textarea" },
      { key: "booking_cta_label", label: "Booking CTA label", type: "text" },
      { key: "booking_cta_url", label: "Booking CTA URL", type: "text" },
    ],
  },
  page_privacy: {
    overline: "Page · Legal",
    title: "Privacy Policy",
    description: "Body supports a light markdown subset: '## Heading', '- bullet', and **bold**.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "updated_label", label: "Last-updated label", type: "text" },
      { key: "updated_value", label: "Last-updated value", type: "text" },
      { key: "body", label: "Policy body", type: "textarea", rows: 20, hint: "Use '## ' for headings, '- ' for bullets, **double-asterisks** for bold." },
    ],
  },
  page_terms: {
    overline: "Page · Legal",
    title: "Terms of Use",
    description: "Body supports a light markdown subset: '## Heading', '- bullet', and **bold**.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "updated_label", label: "Last-updated label", type: "text" },
      { key: "updated_value", label: "Last-updated value", type: "text" },
      { key: "body", label: "Terms body", type: "textarea", rows: 20, hint: "Use '## ' for headings, '- ' for bullets, **double-asterisks** for bold." },
    ],
  },
  page_cookies: {
    overline: "Page · Legal",
    title: "Cookie Policy",
    description: "Body supports a light markdown subset: '## Heading', '- bullet', and **bold**.",
    fields: [
      { key: "meta_title", label: "Meta title (SEO)", type: "text" },
      { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
      { key: "hero_overline", label: "Hero overline", type: "text" },
      { key: "hero_heading", label: "Hero heading", type: "text" },
      { key: "hero_subheading", label: "Hero subheading", type: "text" },
      { key: "updated_label", label: "Last-updated label", type: "text" },
      { key: "updated_value", label: "Last-updated value", type: "text" },
      { key: "body", label: "Cookie policy body", type: "textarea", rows: 20, hint: "Use '## ' for headings, '- ' for bullets, **double-asterisks** for bold." },
    ],
  },
};

const StringList = ({ value = [], onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const setAt = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={v}
            onChange={(e) => setAt(i, e.target.value)}
            className="flex-1 bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-2 text-white"
          />
          <button type="button" onClick={() => remove(i)} className="text-zinc-500 hover:text-red-400 p-2">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand-light hover:text-white mt-2">
        <Plus className="h-4 w-4" /> Add
      </button>
    </div>
  );
};

const StatsList = ({ value = [], onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const setAt = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { value: "", label: "" }]);
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <input
            value={it.value || ""}
            onChange={(e) => setAt(i, { value: e.target.value })}
            placeholder="6:1"
            className="col-span-3 bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-2 text-white"
          />
          <input
            value={it.label || ""}
            onChange={(e) => setAt(i, { label: e.target.value })}
            placeholder="Player-to-Coach Ratio"
            className="col-span-8 bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-2 text-white"
          />
          <button type="button" onClick={() => remove(i)} className="col-span-1 text-zinc-500 hover:text-red-400 p-2 justify-self-end">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand-light hover:text-white mt-2">
        <Plus className="h-4 w-4" /> Add stat
      </button>
    </div>
  );
};

export const SectionEditor = () => {
  const { sectionKey } = useParams();
  const schema = SCHEMAS[sectionKey];
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const { reload: reloadPreview } = usePreview();

  useEffect(() => {
    if (!schema) return;
    setData(null);
    api.get(`/cms/sections/${sectionKey}`).then(({ data }) => {
      setData(data.data || {});
      setOriginal(JSON.stringify(data.data || {}));
    });
  }, [sectionKey, schema]);

  const dirty = useMemo(() => data !== null && JSON.stringify(data) !== original, [data, original]);

  if (!schema) return <div className="text-zinc-400">Unknown section: {sectionKey}</div>;
  if (data === null) return <div className="text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const update = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const onSave = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put(`/cms/sections/${sectionKey}`, { data });
      setData(saved.data);
      setOriginal(JSON.stringify(saved.data));
      toast.success("Saved.");
      reloadPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    setData(JSON.parse(original));
  };

  return (
    <>
      <PageHeader overline={schema.overline} title={schema.title} description={schema.description} />
      <Card>
        <div className="grid gap-6">
          {schema.fields.map((f) => {
            if (f.type === "image") {
              return (
                <ImageUpload
                  key={f.key}
                  label={f.label}
                  value={data[f.key] || ""}
                  onChange={(v) => update(f.key, v)}
                  testId={`field-${f.key}`}
                />
              );
            }
            if (f.type === "string-list") {
              return (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  <StringList value={data[f.key]} onChange={(v) => update(f.key, v)} />
                </Field>
              );
            }
            if (f.type === "stats-list") {
              return (
                <Field key={f.key} label={f.label}>
                  <StatsList value={data[f.key]} onChange={(v) => update(f.key, v)} />
                </Field>
              );
            }
            if (f.type === "textarea") {
              return (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  <Textarea value={data[f.key] || ""} rows={f.rows || 4} onChange={(v) => update(f.key, v)} />
                </Field>
              );
            }
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <Input value={data[f.key] || ""} onChange={(v) => update(f.key, v)} />
              </Field>
            );
          })}
        </div>
      </Card>
      <SaveBar saving={saving} dirty={dirty} onSave={onSave} onReset={onReset} />
    </>
  );
};
